import React, { useEffect, useMemo, useState } from "react";
import SEO from "../components/SEO";
import {
  ShieldCheck,
  Truck,
  MapPin,
  Phone,
  ExternalLink,
  ShoppingBag,
  ArrowUpRight,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { FLOWHUB_MENU_URL } from "../lib/flowhub";
import { MEDIA } from "../lib/constants";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ext = { target: "_blank", rel: "noopener noreferrer" };

// Mood palettes (soft pastels for light theme)
const MOOD = {
  Sativa: { glow: "from-amber-200/70 via-orange-200/30", chip: "text-amber-700 bg-amber-50 border-amber-200" },
  "Sativa-dom": { glow: "from-amber-200/70 via-orange-200/30", chip: "text-amber-700 bg-amber-50 border-amber-200" },
  Indica: { glow: "from-fuchsia-200/70 via-purple-200/30", chip: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200" },
  "Indica-dom": { glow: "from-fuchsia-200/70 via-purple-200/30", chip: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200" },
  "50/50": { glow: "from-emerald-200/70 via-teal-200/30", chip: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  Hybrid: { glow: "from-emerald-200/70 via-teal-200/30", chip: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  "Hybrid 50/50": { glow: "from-emerald-200/70 via-teal-200/30", chip: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  CBD: { glow: "from-sky-200/70 via-blue-200/30", chip: "text-sky-700 bg-sky-50 border-sky-200" },
};
const DEFAULT_MOOD = { glow: "from-brand/20 via-brand/5", chip: "text-brand bg-brand/5 border-brand/20" };

const CATEGORY_FILTERS = [
  { label: "All", match: null },
  { label: "Flower", match: ["Flower"] },
  { label: "Prerolls", match: ["Joints", "Pre-Rolls", "Preroll"] },
  { label: "Vapes", match: ["Cartridges", "Vape", "Disposables"] },
  { label: "Edibles", match: ["Edibles"] },
];

function EditorialProductCard({ product, index }) {
  const mood = MOOD[product.species] || DEFAULT_MOOD;
  return (
    <a
      href={product.url}
      {...ext}
      className="group relative flex flex-col"
      data-testid={`store-product-${index}`}
    >
      {/* Pedestal — floating image with mood glow */}
      <div className="relative h-[300px] sm:h-[340px] flex items-end justify-center overflow-hidden">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[88%] h-[60%] pointer-events-none">
          <div
            className={`absolute inset-0 bg-gradient-radial ${mood.glow} to-transparent rounded-[50%] blur-3xl`}
          />
        </div>
        {product.image ? (
          <img
            src={product.image}
            alt={`${product.name}${product.brand ? ` by ${product.brand}` : ""}`}
            loading="lazy"
            className="relative z-10 w-[78%] h-auto max-h-[85%] object-contain drop-shadow-[0_20px_25px_rgba(10,10,10,0.15)] group-hover:translate-y-[-6px] group-hover:scale-[1.04] transition-transform duration-700 ease-out"
          />
        ) : (
          <div className="relative z-10 w-[60%] aspect-square flex items-center justify-center">
            <span className="font-serif italic text-5xl text-ink/15">
              {product.name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </span>
          </div>
        )}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[80%] h-px bg-gradient-to-r from-transparent via-ink/15 to-transparent" />
      </div>

      {/* Info */}
      <div className="pt-5 pb-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] mb-2">
          <span className="text-ink/40">— {product.category || "Cannabis"}</span>
          {product.species && (
            <>
              <span className="w-1 h-1 rounded-full bg-ink/20" />
              <span className={`${mood.chip} border px-2 py-0.5`}>{product.species}</span>
            </>
          )}
        </div>
        <h3 className="font-serif text-xl sm:text-2xl leading-tight tracking-tight text-ink group-hover:text-brand transition-colors duration-300 line-clamp-2 min-h-[3rem]">
          {product.name}
        </h3>
        {product.brand && (
          <div className="mt-1.5 text-sm italic font-serif text-ink/50">
            by {product.brand}
          </div>
        )}
        <div className="mt-4 flex items-end justify-between">
          <div className="flex items-baseline gap-2">
            {product.price && (
              <span
                className="font-serif text-3xl sm:text-4xl text-ink tracking-tight leading-none"
                style={{ fontFeatureSettings: '"tnum"' }}
              >
                {product.price}
              </span>
            )}
            {product.size && (
              <span className="text-ink/40 text-[10px] uppercase tracking-[0.25em] leading-none pb-1">
                /{product.size}
              </span>
            )}
          </div>
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-brand opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
            Shop
            <ArrowUpRight size={12} />
          </div>
        </div>
      </div>
    </a>
  );
}

export default function Store() {
  const [data, setData] = useState({ products: [], source: null, count: 0, fetched_at: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState("All");
  const [showScrollHint, setShowScrollHint] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      setShowScrollHint(window.scrollY < 100);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleScrollDown = () => {
    const target = document.getElementById("menu-inventory");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: window.innerHeight * 0.9, behavior: "smooth" });
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/menu`);
        const json = await res.json();
        if (!mounted) return;
        setData(json);
        if (json.error && (!json.products || !json.products.length)) {
          setError(json.error);
        }
      } catch (e) {
        if (mounted) setError(e.message || "Failed to load menu");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const f = CATEGORY_FILTERS.find((x) => x.label === active);
    let list = data.products;
    if (f && f.match) {
      list = list.filter((p) =>
        f.match.some((m) => (p.category || "").toLowerCase().includes(m.toLowerCase()))
      );
    }
    // Always prefer products with images first, then ones without
    const withImg = list.filter((p) => p.image);
    const noImg = list.filter((p) => !p.image);
    return [...withImg, ...noImg];
  }, [data.products, active]);

  const refreshedAt = useMemo(() => {
    if (!data.fetched_at) return null;
    const d = new Date(data.fetched_at * 1000);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }, [data.fetched_at]);

  return (
    <>
      <SEO
        title="Cannabis Menu · District Wellness Center | Shop Live Inventory in DC"
        description="Shop the live District Wellness Center menu — premium flower, prerolls, vapes and edibles. Auto-synced in real-time from our Georgia Ave dispensary in Washington, DC."
        path="/menu"
        breadcrumbs={[{ name: "Menu", path: "/menu" }]}
      />

      <section
        id="store"
        className="relative bg-white text-ink overflow-hidden"
        data-testid="store-page"
      >
        {/* Soft color washes */}
        <div className="absolute top-0 right-0 w-[60%] h-[600px] bg-gradient-radial from-brand/8 via-fuchsia-200/10 to-transparent blur-3xl pointer-events-none" />

        {/* NEW CUSTOMER PROMO BANNER — highly visible on the menu page */}
        <div className="relative max-w-7xl mx-auto px-6 pt-10" data-testid="menu-promo-banner">
          <a
            href={MEDIA.freeJointPromo}
            target="_blank"
            rel="noopener noreferrer"
            className="group grid sm:grid-cols-5 gap-0 border border-ink/10 bg-white overflow-hidden hover:border-brand transition-colors"
            data-testid="menu-promo-link"
          >
            <div className="sm:col-span-2 aspect-square sm:aspect-auto overflow-hidden bg-white">
              <img
                src={MEDIA.freeJointPromo}
                alt="First joint's on us — free pre-roll for new customers"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                loading="lazy"
              />
            </div>
            <div className="sm:col-span-3 p-8 sm:p-10 flex flex-col justify-center">
              <div className="text-[10px] uppercase tracking-[0.35em] text-brand mb-4">
                New Customer Deal
              </div>
              <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[0.95] tracking-tight">
                First joint&rsquo;s <em className="text-brand not-italic italic">on us</em>.
              </h3>
              <p className="mt-4 text-base sm:text-lg text-ink-muted leading-relaxed">
                New here? First-time customers get a free pre-roll on your first visit.
                Just show this deal at the register to redeem.
              </p>
              <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-ink-muted">
                21+ with valid ID · One per person · While supplies last
              </div>
            </div>
          </a>
        </div>

        {/* HERO */}
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-brand mb-5">
            <span className="w-6 h-px bg-brand" />
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Live · Synced from Flowhub
          </div>

          <div className="grid lg:grid-cols-3 gap-10 items-end">
            <div className="lg:col-span-2">
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink">
                Cannabis <em className="text-brand">menu</em> · Washington DC.
              </h1>
              <p className="mt-6 text-ink/60 max-w-2xl text-base sm:text-lg leading-relaxed">
                Every product below auto-syncs from our live Flowhub inventory
                at 4915 Georgia Ave NW. Reserve online for in-store pickup or
                order same-day delivery across Washington, DC.
              </p>
              {refreshedAt && (
                <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-ink/35">
                  Last sync · {refreshedAt}
                </p>
              )}
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4 text-sm">
              <li className="flex items-start gap-3" data-testid="store-feature-medical">
                <div className="w-9 h-9 bg-emerald-50 flex items-center justify-center shrink-0">
                  <ShieldCheck size={16} className="text-emerald-600" />
                </div>
                <div>
                  <div className="text-ink font-medium">Medical-grade</div>
                  <div className="text-ink/50 text-xs">DC-licensed dispensary</div>
                </div>
              </li>
              <li className="flex items-start gap-3" data-testid="store-feature-pickup">
                <div className="w-9 h-9 bg-brand/10 flex items-center justify-center shrink-0">
                  <MapPin size={16} className="text-brand" />
                </div>
                <div>
                  <div className="text-ink font-medium">Pickup ready</div>
                  <div className="text-ink/50 text-xs">4915 Georgia Ave NW</div>
                </div>
              </li>
              <li className="flex items-start gap-3" data-testid="store-feature-delivery">
                <div className="w-9 h-9 bg-sky-50 flex items-center justify-center shrink-0">
                  <Truck size={16} className="text-sky-600" />
                </div>
                <div>
                  <div className="text-ink font-medium">DC delivery</div>
                  <div className="text-ink/50 text-xs">Same-day available</div>
                </div>
              </li>
            </ul>
          </div>

          <div className="mt-12 flex flex-wrap gap-4 items-center">
            <a
              href={FLOWHUB_MENU_URL}
              {...ext}
              className="inline-flex items-center gap-3 bg-brand text-white px-7 py-4 text-[11px] uppercase tracking-[0.3em] font-bold hover:bg-brand/90 hover:shadow-[0_8px_30px_-8px_rgba(252,121,51,0.6)] transition-all"
              data-testid="store-browse-full-menu"
            >
              <ShoppingBag size={16} />
              Browse Full Menu
              <ExternalLink size={14} />
            </a>
            <a
              href="tel:+12026217800"
              className="inline-flex items-center gap-3 border border-ink/15 text-ink px-7 py-4 text-[11px] uppercase tracking-[0.3em] hover:border-brand hover:text-brand transition-colors"
              data-testid="store-call-cta"
            >
              <Phone size={14} />
              Call (202) 621-7800
            </a>
          </div>
        </div>

        {/* SCROLL INDICATOR — pill button "Scroll" with animated chevron */}
        <div
          className={`fixed left-0 right-0 bottom-6 z-30 flex justify-center pointer-events-none transition-opacity duration-500 ${
            showScrollHint ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={!showScrollHint}
        >
          <button
            type="button"
            onClick={handleScrollDown}
            className="pointer-events-auto group inline-flex items-center gap-3 bg-ink text-white pl-6 pr-3 py-2.5 rounded-full shadow-[0_10px_30px_-8px_rgba(10,10,10,0.35)] hover:bg-brand hover:shadow-[0_12px_35px_-8px_rgba(252,121,51,0.55)] transition-all duration-300"
            data-testid="menu-scroll-hint"
            aria-label="Scroll down to menu inventory"
          >
            <span className="text-[11px] uppercase tracking-[0.3em] font-semibold">
              Scroll
            </span>
            <span className="w-8 h-8 rounded-full bg-white/15 group-hover:bg-white/25 flex items-center justify-center">
              <ChevronDown
                size={16}
                className="animate-bounce"
                strokeWidth={2.5}
              />
            </span>
          </button>
        </div>

        {/* IN STOCK NOW */}
        <div id="menu-inventory" className="relative max-w-7xl mx-auto px-6 pb-20 scroll-mt-24">
          <div className="flex items-end justify-between border-b border-ink/10 pb-6 mb-8">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">
                In stock now
              </h2>
              <p className="text-ink/50 text-xs uppercase tracking-[0.25em] mt-2">
                {loading
                  ? "Loading live inventory…"
                  : error
                  ? "Live sync unavailable — visit the full menu →"
                  : `${filtered.length} products available · auto-synced from Flowhub`}
              </p>
            </div>
            <a
              href={FLOWHUB_MENU_URL}
              {...ext}
              className="hidden sm:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-ink hover:text-brand border-b border-ink/20 hover:border-brand pb-1 transition-colors"
              data-testid="store-view-all-link"
            >
              View all <ArrowUpRight size={12} />
            </a>
          </div>

          {!loading && !error && data.products.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10" data-testid="store-filter-row">
              {CATEGORY_FILTERS.map((f) => {
                const isActive = active === f.label;
                return (
                  <button
                    key={f.label}
                    onClick={() => setActive(f.label)}
                    className={`px-4 py-2 text-[11px] uppercase tracking-[0.25em] transition-all border ${
                      isActive
                        ? "bg-ink text-white border-ink"
                        : "bg-white text-ink/60 border-ink/10 hover:border-ink hover:text-ink"
                    }`}
                    data-testid={`store-filter-${f.label.toLowerCase()}`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-24 text-ink/50" data-testid="store-loading">
              <Loader2 size={28} className="text-brand animate-spin mb-3" />
              <p className="text-[10px] uppercase tracking-[0.3em]">Loading live inventory…</p>
            </div>
          )}

          {!loading && error && (
            <div className="border border-ink/10 bg-white p-10 text-center" data-testid="store-error">
              <p className="text-ink/70 mb-4">We couldn&rsquo;t reach the live menu right now.</p>
              <a
                href={FLOWHUB_MENU_URL}
                {...ext}
                className="inline-flex items-center gap-2 bg-brand text-white px-6 py-3 text-[11px] uppercase tracking-[0.3em] font-bold"
              >
                Open Flowhub menu <ExternalLink size={14} />
              </a>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-14 sm:gap-y-16"
              data-testid="store-product-grid"
            >
              {filtered.map((p, i) => (
                <EditorialProductCard key={p.url + i} product={p} index={i} />
              ))}
            </div>
          )}

          <div className="flex justify-center mt-20">
            <a
              href={FLOWHUB_MENU_URL}
              {...ext}
              className="inline-flex items-center gap-3 bg-brand text-white px-8 py-4 text-[11px] uppercase tracking-[0.3em] font-bold hover:bg-brand/90 hover:shadow-[0_8px_30px_-8px_rgba(252,121,51,0.6)] transition-all"
              data-testid="store-bottom-browse-full-menu"
            >
              <ShoppingBag size={16} />
              Open full menu in Flowhub
              <ExternalLink size={14} />
            </a>
          </div>
        </div>

        {/* HELP */}
        <div className="relative max-w-5xl mx-auto px-6 pb-24">
          <div className="relative bg-gradient-to-br from-brand/[0.06] via-white to-fuchsia-100/40 border border-ink/10 p-8 sm:p-12 text-center overflow-hidden">
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-brand/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-fuchsia-300/20 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <h3 className="font-serif text-2xl sm:text-3xl text-ink mb-3">
                Need a recommendation?
              </h3>
              <p className="text-ink/60 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
                Our budtenders are here to help patients new and experienced
                navigate strains, formats and dosing. Call us or stop by
                Georgia Ave NW — we&rsquo;d love to meet you.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                <a
                  href="tel:+12026217800"
                  className="inline-flex items-center justify-center gap-2 bg-brand text-white px-6 py-3 text-[11px] uppercase tracking-[0.3em] font-bold hover:bg-brand/90 transition-colors"
                  data-testid="store-help-call"
                >
                  <Phone size={14} />
                  Call (202) 621-7800
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 border border-ink/20 text-ink px-6 py-3 text-[11px] uppercase tracking-[0.3em] hover:border-brand hover:text-brand transition-colors"
                  data-testid="store-help-contact"
                >
                  Contact us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
